// packages/web-ui/src/AppShell/TabBar/CloseConfirmDialog.tsx
//
// W2-S4 AC #7 — dirty-buffer close confirm dialog. Renders a centred modal
// with Save / Discard / Cancel actions. Esc → Cancel; Enter → Save (default
// focus). For bulk-close paths, the title/body counts the dirty set.
//
// Hand-rolled dialog (no Radix/shadcn dep churn) — uses a simple focus-trap
// loop on Tab/Shift+Tab and a backdrop overlay.

import { useCallback, useEffect, useRef } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import type { TabState } from '../../state/navigation.js';
import { Z_INDEX } from '../responsive-tokens.js';

export interface CloseConfirmDialogProps {
  open: boolean;
  /** Tabs that the user is attempting to close. Length 1 → singular dialog. */
  targets: TabState[];
  onConfirm: (action: 'save' | 'discard') => void;
  onCancel: () => void;
}

export function CloseConfirmDialog({
  open,
  targets,
  onConfirm,
  onCancel,
}: CloseConfirmDialogProps): JSX.Element | null {
  const saveBtnRef = useRef<HTMLButtonElement>(null);
  const discardBtnRef = useRef<HTMLButtonElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  // Focus the Save button on open (default action per AC #7).
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      saveBtnRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(handle);
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      // Tab cycle within the three buttons (focus trap).
      if (event.key === 'Tab') {
        const order = [
          saveBtnRef.current,
          discardBtnRef.current,
          cancelBtnRef.current,
        ].filter((b): b is HTMLButtonElement => b !== null);
        if (order.length === 0) return;
        const current = order.indexOf(document.activeElement as HTMLButtonElement);
        const direction = event.shiftKey ? -1 : 1;
        const next = (current + direction + order.length) % order.length;
        event.preventDefault();
        order[next]?.focus();
      }
    },
    [onCancel],
  );

  if (!open) return null;
  if (targets.length === 0) return null;

  const single = targets.length === 1 ? targets[0] : null;
  const title = single
    ? `Save changes to ${single.label}?`
    : `Save changes to ${targets.length} files?`;
  const body = single
    ? `You have unsaved changes in ${single.label}. What would you like to do?`
    : `You have unsaved changes in ${targets.length} files. What would you like to do?`;

  return (
    <div
      role="presentation"
      data-testid="close-confirm-backdrop"
      onClick={(event) => {
        // Click the backdrop closes the dialog (treat as Cancel).
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.4)',
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="close-confirm-title"
        aria-describedby="close-confirm-body"
        data-testid="close-confirm-dialog"
        className="bg-cream-50 border-taupe-500 rounded-none border"
        style={{ minWidth: 360, padding: 20 }}
      >
        <h2
          id="close-confirm-title"
          data-testid="close-confirm-title"
          className="font-semibold"
          style={{ marginBottom: 8 }}
        >
          {title}
        </h2>
        <p
          id="close-confirm-body"
          data-testid="close-confirm-body"
          style={{ marginBottom: 16 }}
        >
          {body}
        </p>
        <div
          role="group"
          aria-label="Close confirmation actions"
          style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
        >
          <button
            ref={saveBtnRef}
            type="button"
            data-testid="close-confirm-save"
            onClick={() => onConfirm('save')}
            className="px-3 py-1 rounded bg-[var(--accent-taupe)] text-white"
          >
            Save
          </button>
          <button
            ref={discardBtnRef}
            type="button"
            data-testid="close-confirm-discard"
            onClick={() => onConfirm('discard')}
            className="px-3 py-1 rounded border border-red-600 text-red-700"
          >
            Discard
          </button>
          <button
            ref={cancelBtnRef}
            type="button"
            data-testid="close-confirm-cancel"
            onClick={onCancel}
            className="px-3 py-1 rounded border"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
